import React, {useState, useEffect, useRef} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import useAuth from '../../../../hooks/useAuth'
import {Spinner} from 'react-bootstrap'

export function ForgotPassword() {
  const intl = useIntl()
  
  const initialValues = {
    username: '',
  }

  const forgotPasswordSchema = Yup.object().shape({
    username: Yup.string()
      .email(intl.formatMessage({id: 'FORM.ERROR.EMAIL.WRONG_FORMAT'}))
      .min(3, intl.formatMessage({id: 'FORM.ERROR.SYMBOL.MIN'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'FORM.ERROR.SYMBOL.MAX'}, {max: 50}))
      .required(intl.formatMessage({id: 'FORM.ERROR.REQUIRED'})),
  })

  const {requestResetPassword, actionLoading} = useAuth()

  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [isRequestPending, setIsRequestPending] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const hasSubmittedRef = useRef(false)
  
  // Prevent resubmission on page refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRequestPending) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isRequestPending])
  
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values: {username: string}, {setStatus, setSubmitting, resetForm}) => {
      // Prevent multiple submissions
      if (isRequestPending || hasSubmittedRef.current) {
        return
      }
      
      hasSubmittedRef.current = true
      setIsRequestPending(true)
      
      requestResetPassword(values)
        .then((action) => {
          const payload: any = action.payload
          if (payload.success) {
            setHasErrors(false)
            setEmailSent(true)
            // Clear the form after successful submission
            resetForm()
          } else {
            setHasErrors(true)
            hasSubmittedRef.current = false
          }
        })
        .catch((error) => {
          setHasErrors(true)
          hasSubmittedRef.current = false
        })
        .finally(() => {
          setSubmitting(false)
          setIsRequestPending(false)
        })
    },
  })

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    formik.handleSubmit(e)
  }

  return (
    <>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={handleFormSubmit}
      >
        <div className='text-center mb-10'>
          {/* begin::Title */}
          <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'MENU.FORGOT_PASSWORD'})}</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className='text-white fw-bold fs-4'>
            {intl.formatMessage({id: 'MENU.ENTER_EMAIL_TO_RESET_PASSWORD'})}
          </div>
          {/* end::Link */}
        </div>

        {/* begin::Title */}
        {hasErrors === true && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>
              <div className='text-danger'>
                {intl.formatMessage({id: 'ERROR.SENT_PASSWORD_RESET'})}
              </div>
            </div>
          </div>
        )}

        {hasErrors === false && (
          <div className='mb-10 bg-light-info p-8 rounded'>
            <div className='text-info'>
              {intl.formatMessage({id: 'SUCCESS.SENT_PASSWORD_RESET'})}
            </div>
          </div>
        )}
        {/* end::Title */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <label className='form-label fs-6 fw-bolder text-dark'>
            {intl.formatMessage({id: 'INPUT.EMAIL.LABEL'})}
          </label>
          <input
            placeholder={intl.formatMessage({id: 'INPUT.EMAIL.PLACEHOLDER'})}
            {...formik.getFieldProps('username')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid border border-danger':
                  formik.touched.username && formik.errors.username,
              },
              {
                'is-valid ': formik.touched.username && !formik.errors.username,
              }
            )}
            type='email'
            name='username'
            autoComplete='off'
            disabled={emailSent}
          />
          {formik.touched.username && formik.errors.username && (
            <div className='fv-plugins-message-container py-2'>
              <span className='text-danger fw-bold' role='alert'>
                {formik.errors.username}
              </span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          {actionLoading || isRequestPending ? (
            <button className='btn btn-primary btn-lg fw-bolder me-4' disabled>
              <Spinner role='status' animation='border' />
            </button>
          ) : emailSent ? (
            <Link to='/auth/login'>
              <button
                type='button'
                className='btn btn-primary btn-lg fw-bolder'
              >
                {intl.formatMessage({id: 'MENU.LOGIN'})}
              </button>
            </Link>
          ) : (
            <>
              <Link to='/auth/login'>
                <button
                  type='button'
                  id='kt_login_password_reset_form_cancel_button'
                  className='btn btn-lg fw-bolder'
                  disabled={formik.isSubmitting || isRequestPending}
                >
                  {intl.formatMessage({id: 'MENU.CANCEL'})}
                </button>
              </Link>
              <button
                type='submit'
                className='btn btn-primary btn-lg fw-bolder me-4'
                disabled={formik.isSubmitting || !formik.isValid || isRequestPending || emailSent}
              >
                <span className='indicator-label'>
                  {intl.formatMessage({id: 'MENU.SUBMIT'})}
                </span>
              </button>
            </>
          )}
        </div>
        {/* end::Form group */}
      </form>
    </>
  )
}
